import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { StructureCastClient } from "serviceStores/mock/structurecast-clients.mock";
import type { StructureCastProject } from "serviceStores/mock/structurecast-projects.mock";
import type { StructureCastStructure } from "serviceStores/mock/structurecast-structures.mock";
import type { StructureCastComponent } from "serviceStores/mock/structurecast-components.mock";
import type { StructureCastCastingGroup } from "serviceStores/mock/structurecast-casting-groups.mock";
import type { StructureCastCastingDay } from "serviceStores/mock/structurecast-casting-days.mock";
import type { StructureCastMoldAssignment } from "serviceStores/mock/structurecast-mold-assignments.mock";
import type { StructureCastShipment } from "serviceStores/mock/structurecast-shipments.mock";
import type { StructureCastShipmentLoad } from "serviceStores/mock/structurecast-shipment-loads.mock";
import type { StructureCastPreProductionMeeting } from "serviceStores/mock/structurecast-pre-production-meetings.mock";
import type { StructureCastCastingTable } from "serviceStores/mock/structurecast-casting-tables.mock";
import type { StructureCastYardZone } from "serviceStores/mock/structurecast-yard-zones.mock";
import type { StructureCastEstimate } from "serviceStores/mock/structurecast-estimates.mock";
import type {
  StructureCastEstimateLineItem,
  EstimateComponentDefinition,
} from "serviceStores/mock/structurecast-estimate-line-items.mock";
import type { StructureCastQcInspection } from "serviceStores/mock/structurecast-qc-inspections.mock";
import type {
  StructureCastBatchTicket,
  CylinderTestResult,
} from "serviceStores/mock/structurecast-batch-tickets.mock";
import type { StructureCastRawMaterial } from "serviceStores/mock/structurecast-raw-materials.mock";
import type { StructureCastVendor } from "serviceStores/mock/structurecast-vendors.mock";
import type {
  StructureCastPurchaseOrder,
  StructureCastPOStatus,
} from "serviceStores/mock/structurecast-purchase-orders.mock";
import type { StructureCastPurchaseOrderLineItem } from "serviceStores/mock/structurecast-purchase-order-line-items.mock";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyStore = any;

export interface StructureCompletionRow {
  structureId: string;
  markNumber: string;
  stationReference: string;
  overallLength: number;
  overallWidth: number;
  overallHeight: number;
  totalWeight: number;
  totalCubicYards: number;
  status: StructureCastStructure["status"];
  totalComponents: number;
  castComponents: number;
  components: StructureCastComponent[];
}

export interface UnscheduledComponentRow {
  componentId: string;
  structureId: string;
  markNumber: string;
  controlNumber: string;
  componentType: string;
  weight: number;
  cubicYards: number;
  stationReference: string;
}

export interface CastingGridRow {
  castingDay: StructureCastCastingDay;
  moldPositions: Map<number | "sump", StructureCastMoldAssignment[]>;
  totalCubicYards: number;
}

export interface UnifiedCastingGridRow {
  date: string; // YYYY-MM-DD
  castingDay: StructureCastCastingDay | null;
  group: StructureCastCastingGroup | null;
  moldPositions: Map<number | "sump", StructureCastMoldAssignment[]>;
  totalCubicYards: number;
  isFirstDayOfGroup: boolean;
}

export interface GroupProgress {
  groupId: string;
  groupName: string;
  groupNumber: number;
  status: StructureCastCastingGroup["status"];
  totalDays: number;
  completedDays: number;
  totalAssignments: number;
  completedAssignments: number;
  progressPercent: number;
}

export interface CylinderTestRow {
  ticket: StructureCastBatchTicket;
  test: CylinderTestResult;
}

export interface CompressionTestSummary {
  specifiedPsi: number;
  totalCylinders: number;
  count7Day: number;
  count14Day: number;
  count28Day: number;
  avg7Day: number;
  avg14Day: number;
  avg28Day: number;
  min28Day: number;
  max28Day: number;
  passRate28Day: number; // 0–1
}

export interface BatchQualitySummary {
  totalBatches: number;
  avgSlump: number;
  slumpSpec: number;
  slumpPassRate: number; // 0–1
  avgAir: number;
  airSpec: number;
  airPassRate: number;   // 0–1
  avgTemp: number;
}

export interface OpenQcItemRow {
  inspectionId: string;
  componentId: string;
  controlNumber: string;
  structureId: string;
  markNumber: string;
  inspectionType: StructureCastQcInspection["inspectionType"];
  status: StructureCastQcInspection["status"];
  inspectionDate: string;
  daysSincePour: number;
  moldAssignmentId: string;
}

class StructureCastProductionScheduleStore {
  rootStore: RootStore;

  // UI state
  currentTab = 0;
  selectedProjectId: string | null = "sc-proj-1";
  selectedCastingGroupId: string | null = null;
  dateRangeStart: string = "2026-01-12";
  dateRangeEnd: string = "2026-04-15";
  searchQuery = "";
  efficiencyPeriod: "daily" | "weekly" | "monthly" = "weekly";
  floorPlanDate: string = "2026-01-12";

  // Dialog state
  dialogPrefill: {
    componentId?: string;
    castingDayId?: string;
    moldPosition?: number | "sump";
  } | null = null;
  selectedMoldAssignmentId: string | null = null;
  estimateDialogContext: { estimateId?: string; lineItemId?: string } | null = null;
  labelPrintContext: {
    mode: "single" | "day" | "load";
    componentIds: string[];     // explicit list for "single" mode
    castingDayId?: string;       // resolved at render time for "day" mode
    shipmentLoadId?: string;     // resolved at render time for "load" mode
  } | null = null;

  // Vendor / PO tab state
  editingVendor: StructureCastVendor | null = null;
  editingPurchaseOrder: StructureCastPurchaseOrder | null = null;
  editingPurchaseOrderLineItems: StructureCastPurchaseOrderLineItem[] = [];
  vendorPoSearchQuery = "";
  vendorStatusFilter = "all";
  vendorCategoryFilter = "all";
  poStatusFilter = "all";
  poVendorFilter: string | null = null;

  // Loading state
  loading = false;
  error: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  // --- Mock store accessors ---

  private get _clientStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastClientsStore;
  }
  private get _projectStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastProjectsStore;
  }
  private get _structureStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastStructuresStore;
  }
  private get _componentStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastComponentsStore;
  }
  private get _castingGroupStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastCastingGroupsStore;
  }
  private get _castingDayStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastCastingDaysStore;
  }
  private get _moldAssignmentStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastMoldAssignmentsStore;
  }
  private get _shipmentStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastShipmentsStore;
  }
  private get _shipmentLoadStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastShipmentLoadsStore;
  }
  private get _preProductionMeetingStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastPreProductionMeetingsStore;
  }
  private get _castingTableStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastCastingTablesStore;
  }
  private get _yardZoneStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastYardZonesStore;
  }
  private get _estimateStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastEstimatesStore;
  }
  private get _estimateLineItemStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastEstimateLineItemsStore;
  }
  private get _qcInspectionStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastQcInspectionsStore;
  }
  private get _batchTicketStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastBatchTicketsStore;
  }
  private get _rawMaterialStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastRawMaterialsStore;
  }
  private get _vendorStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastVendorsStore;
  }
  private get _purchaseOrderStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastPurchaseOrdersStore;
  }
  private get _poLineItemStore(): AnyStore {
    return (this.rootStore as AnyStore).structureCastPurchaseOrderLineItemsStore;
  }

  // --- Computed: raw entity lists ---

  get clients(): StructureCastClient[] {
    return (this._clientStore?.entities as StructureCastClient[]) || [];
  }

  get projects(): StructureCastProject[] {
    return (this._projectStore?.entities as StructureCastProject[]) || [];
  }

  get structures(): StructureCastStructure[] {
    return (this._structureStore?.entities as StructureCastStructure[]) || [];
  }

  get components(): StructureCastComponent[] {
    return (this._componentStore?.entities as StructureCastComponent[]) || [];
  }

  get castingGroups(): StructureCastCastingGroup[] {
    return (this._castingGroupStore?.entities as StructureCastCastingGroup[]) || [];
  }

  get castingDays(): StructureCastCastingDay[] {
    return (this._castingDayStore?.entities as StructureCastCastingDay[]) || [];
  }

  get moldAssignments(): StructureCastMoldAssignment[] {
    return (this._moldAssignmentStore?.entities as StructureCastMoldAssignment[]) || [];
  }

  get shipments(): StructureCastShipment[] {
    return (this._shipmentStore?.entities as StructureCastShipment[]) || [];
  }

  get shipmentLoads(): StructureCastShipmentLoad[] {
    return (this._shipmentLoadStore?.entities as StructureCastShipmentLoad[]) || [];
  }

  get preProductionMeetings(): StructureCastPreProductionMeeting[] {
    return (this._preProductionMeetingStore?.entities as StructureCastPreProductionMeeting[]) || [];
  }

  get castingTables(): StructureCastCastingTable[] {
    return (this._castingTableStore?.entities as StructureCastCastingTable[]) || [];
  }

  get yardZones(): StructureCastYardZone[] {
    return (this._yardZoneStore?.entities as StructureCastYardZone[]) || [];
  }

  get estimates(): StructureCastEstimate[] {
    return (this._estimateStore?.entities as StructureCastEstimate[]) || [];
  }

  get estimateLineItems(): StructureCastEstimateLineItem[] {
    return (this._estimateLineItemStore?.entities as StructureCastEstimateLineItem[]) || [];
  }

  get qcInspections(): StructureCastQcInspection[] {
    return (this._qcInspectionStore?.entities as StructureCastQcInspection[]) || [];
  }

  get batchTickets(): StructureCastBatchTicket[] {
    return (this._batchTicketStore?.entities as StructureCastBatchTicket[]) || [];
  }

  get rawMaterials(): StructureCastRawMaterial[] {
    return (this._rawMaterialStore?.entities as StructureCastRawMaterial[]) || [];
  }

  get vendors(): StructureCastVendor[] {
    return (this._vendorStore?.entities as StructureCastVendor[]) || [];
  }

  get purchaseOrders(): StructureCastPurchaseOrder[] {
    return (this._purchaseOrderStore?.entities as StructureCastPurchaseOrder[]) || [];
  }

  get purchaseOrderLineItems(): StructureCastPurchaseOrderLineItem[] {
    return (this._poLineItemStore?.entities as StructureCastPurchaseOrderLineItem[]) || [];
  }

  // --- Vendor / PO filtered lists, helpers, summaries ---

  get filteredVendors(): StructureCastVendor[] {
    let result = this.vendors;
    if (this.vendorStatusFilter !== "all") {
      result = result.filter((v) => v.status === this.vendorStatusFilter);
    }
    if (this.vendorCategoryFilter !== "all") {
      result = result.filter((v) =>
        v.categories.includes(this.vendorCategoryFilter as StructureCastVendor["categories"][number])
      );
    }
    if (this.vendorPoSearchQuery) {
      const q = this.vendorPoSearchQuery.toLowerCase();
      result = result.filter(
        (v) =>
          v.vendorCode.toLowerCase().includes(q) ||
          v.vendorName.toLowerCase().includes(q) ||
          v.contactName.toLowerCase().includes(q) ||
          v.city.toLowerCase().includes(q)
      );
    }
    return result.slice().sort((a, b) => a.vendorName.localeCompare(b.vendorName));
  }

  get filteredPurchaseOrders(): StructureCastPurchaseOrder[] {
    let result = this.purchaseOrders;
    if (this.poStatusFilter !== "all") {
      result = result.filter((p) => p.status === this.poStatusFilter);
    }
    if (this.poVendorFilter) {
      result = result.filter((p) => p.vendorId === this.poVendorFilter);
    }
    if (this.vendorPoSearchQuery) {
      const q = this.vendorPoSearchQuery.toLowerCase();
      const vendorById = new Map(this.vendors.map((v) => [v.id, v]));
      result = result.filter((p) => {
        const vendor = vendorById.get(p.vendorId);
        return (
          p.poNumber.toLowerCase().includes(q) ||
          (vendor?.vendorName.toLowerCase().includes(q) ?? false) ||
          p.notes.toLowerCase().includes(q)
        );
      });
    }
    return result.slice().sort((a, b) => b.orderDate.localeCompare(a.orderDate));
  }

  getVendor = (vendorId: string): StructureCastVendor | undefined => {
    return this.vendors.find((v) => v.id === vendorId);
  };

  getVendorName = (vendorId: string): string => {
    return this.getVendor(vendorId)?.vendorName ?? "Unknown vendor";
  };

  getLineItemsForPO = (purchaseOrderId: string): StructureCastPurchaseOrderLineItem[] => {
    return this.purchaseOrderLineItems
      .filter((li) => li.purchaseOrderId === purchaseOrderId)
      .slice()
      .sort((a, b) => a.id.localeCompare(b.id));
  };

  getPOCountForVendor = (vendorId: string): number => {
    return this.purchaseOrders.filter((p) => p.vendorId === vendorId).length;
  };

  getOpenPOCountForVendor = (vendorId: string): number => {
    return this.purchaseOrders.filter(
      (p) => p.vendorId === vendorId && p.status !== "closed" && p.status !== "cancelled"
    ).length;
  };

  get totalVendors(): number {
    return this.vendors.length;
  }

  get activeVendors(): number {
    return this.vendors.filter((v) => v.status === "active").length;
  }

  get totalPurchaseOrders(): number {
    return this.purchaseOrders.length;
  }

  get openPurchaseOrders(): number {
    return this.purchaseOrders.filter(
      (p) => p.status !== "closed" && p.status !== "cancelled"
    ).length;
  }

  get openPOValue(): number {
    return this.purchaseOrders
      .filter((p) => p.status !== "closed" && p.status !== "cancelled")
      .reduce((sum, p) => sum + p.totalAmount, 0);
  }

  get poStatusBreakdown(): Record<StructureCastPOStatus, number> {
    const buckets: Record<StructureCastPOStatus, number> = {
      draft: 0, sent: 0, acknowledged: 0, partial: 0, received: 0, closed: 0, cancelled: 0,
    };
    for (const po of this.purchaseOrders) {
      buckets[po.status] = (buckets[po.status] || 0) + 1;
    }
    return buckets;
  }

  // --- Vendor / PO UI actions ---

  setVendorPoSearchQuery = (q: string) => { this.vendorPoSearchQuery = q; };
  setVendorStatusFilter = (s: string) => { this.vendorStatusFilter = s; };
  setVendorCategoryFilter = (c: string) => { this.vendorCategoryFilter = c; };
  setPoStatusFilter = (s: string) => { this.poStatusFilter = s; };
  setPoVendorFilter = (id: string | null) => { this.poVendorFilter = id; };
  clearPoVendorFilter = () => { this.poVendorFilter = null; };

  viewVendorPOs = (vendorId: string) => {
    this.poVendorFilter = vendorId;
    this.poStatusFilter = "all";
    this.currentTab = 8; // Purchase Orders tab
  };

  // --- Vendor CRUD ---

  createVendor = async (data: Omit<StructureCastVendor, "id" | "createdAt" | "updatedAt">) => {
    if (!this._vendorStore) return;
    const now = new Date().toISOString();
    await this._vendorStore.create({
      ...data,
      id: `sc-vendor-new-${Date.now()}`,
      createdAt: now,
      updatedAt: now,
    });
    this.editingVendor = null;
  };

  updateVendor = async (vendorId: string, data: Partial<StructureCastVendor>) => {
    if (!this._vendorStore) return;
    await this._vendorStore.patch(vendorId, {
      ...data,
      updatedAt: new Date().toISOString(),
    });
    this.editingVendor = null;
  };

  removeVendor = async (vendorId: string) => {
    if (!this._vendorStore) return;
    await this._vendorStore.remove(vendorId);
  };

  // --- PO CRUD ---

  nextPONumber(): string {
    const year = new Date().getFullYear();
    const prefix = `PO-${year}-`;
    const existing = this.purchaseOrders
      .map((p) => p.poNumber)
      .filter((n) => n.startsWith(prefix))
      .map((n) => parseInt(n.slice(prefix.length), 10))
      .filter((n) => !Number.isNaN(n));
    const next = (existing.length > 0 ? Math.max(...existing) : 0) + 1;
    return `${prefix}${String(next).padStart(3, "0")}`;
  }

  createPurchaseOrder = async (
    poData: Omit<StructureCastPurchaseOrder, "id" | "createdAt" | "updatedAt" | "subtotal" | "totalAmount">,
    lineItems: Array<Omit<StructureCastPurchaseOrderLineItem, "id" | "createdAt" | "updatedAt" | "purchaseOrderId">>
  ) => {
    if (!this._purchaseOrderStore || !this._poLineItemStore) return;
    const now = new Date().toISOString();
    const poId = `sc-po-new-${Date.now()}`;
    const subtotal = +lineItems.reduce((s, li) => s + li.total, 0).toFixed(2);
    const totalAmount = +(subtotal + (poData.taxAmount ?? 0) + (poData.shippingCost ?? 0)).toFixed(2);

    await this._purchaseOrderStore.create({
      ...poData,
      id: poId,
      subtotal,
      totalAmount,
      createdAt: now,
      updatedAt: now,
    });

    for (const [idx, li] of lineItems.entries()) {
      await this._poLineItemStore.create({
        ...li,
        id: `sc-poli-new-${Date.now()}-${idx}`,
        purchaseOrderId: poId,
        createdAt: now,
        updatedAt: now,
      });
    }
    this.editingPurchaseOrder = null;
    this.editingPurchaseOrderLineItems = [];
  };

  updatePurchaseOrder = async (
    poId: string,
    data: Partial<StructureCastPurchaseOrder>,
    lineItems?: Array<Omit<StructureCastPurchaseOrderLineItem, "id" | "createdAt" | "updatedAt" | "purchaseOrderId">>
  ) => {
    if (!this._purchaseOrderStore) return;
    let patch: Partial<StructureCastPurchaseOrder> = {
      ...data,
      updatedAt: new Date().toISOString(),
    };
    if (lineItems) {
      const existing = this.getLineItemsForPO(poId);
      for (const li of existing) {
        await this._poLineItemStore?.remove(li.id);
      }
      const now = new Date().toISOString();
      for (const [idx, li] of lineItems.entries()) {
        await this._poLineItemStore?.create({
          ...li,
          id: `sc-poli-new-${Date.now()}-${idx}`,
          purchaseOrderId: poId,
          createdAt: now,
          updatedAt: now,
        });
      }
      const subtotal = +lineItems.reduce((s, li) => s + li.total, 0).toFixed(2);
      const existingPO = this.purchaseOrders.find((p) => p.id === poId);
      const taxAmount = data.taxAmount ?? existingPO?.taxAmount ?? 0;
      const shippingCost = data.shippingCost ?? existingPO?.shippingCost ?? 0;
      patch = { ...patch, subtotal, totalAmount: +(subtotal + taxAmount + shippingCost).toFixed(2) };
    }
    await this._purchaseOrderStore.patch(poId, patch);
    this.editingPurchaseOrder = null;
    this.editingPurchaseOrderLineItems = [];
  };

  updatePurchaseOrderStatus = async (poId: string, status: StructureCastPOStatus) => {
    if (!this._purchaseOrderStore) return;
    const patch: Record<string, unknown> = { status, updatedAt: new Date().toISOString() };
    if (status === "closed" || status === "received") {
      patch.deliveredDate = new Date().toISOString().split("T")[0];
    }
    await this._purchaseOrderStore.patch(poId, patch);
  };

  removePurchaseOrder = async (poId: string) => {
    if (!this._purchaseOrderStore) return;
    const items = this.getLineItemsForPO(poId);
    for (const li of items) {
      await this._poLineItemStore?.remove(li.id);
    }
    await this._purchaseOrderStore.remove(poId);
  };

  openVendorDialog = (vendor?: StructureCastVendor) => {
    this.editingVendor = vendor ?? null;
    this.rootStore.routerStore.openDialog({ dialogName: "vendorForm" });
  };

  openPurchaseOrderDialog = (po?: StructureCastPurchaseOrder) => {
    this.editingPurchaseOrder = po ?? null;
    this.editingPurchaseOrderLineItems = po
      ? this.getLineItemsForPO(po.id).slice()
      : [];
    this.rootStore.routerStore.openDialog({ dialogName: "purchaseOrderForm" });
  };

  // --- QC / Batch Ticket lookups ---

  getQcInspectionsForComponent = (componentId: string): StructureCastQcInspection[] => {
    return this.qcInspections.filter((i) => i.componentId === componentId);
  };

  getPrePourInspection = (componentId: string): StructureCastQcInspection | undefined => {
    return this.qcInspections.find(
      (i) => i.componentId === componentId && i.inspectionType === "pre_pour"
    );
  };

  getPostPourInspection = (componentId: string): StructureCastQcInspection | undefined => {
    return this.qcInspections.find(
      (i) => i.componentId === componentId && i.inspectionType === "post_pour"
    );
  };

  getBatchTicketsForDay = (castingDayId: string): StructureCastBatchTicket[] => {
    return this.batchTickets.filter((t) => t.castingDayId === castingDayId);
  };

  // --- QC Dashboard aggregations (project-filtered, computed) ---

  get filteredBatchTickets(): StructureCastBatchTicket[] {
    const dayIds = new Set(this.filteredCastingDays.map((d) => d.id));
    return this.batchTickets
      .filter((t) => dayIds.has(t.castingDayId))
      .sort((a, b) => a.date.localeCompare(b.date));
  }

  get filteredQcInspections(): StructureCastQcInspection[] {
    const filteredStructIds = new Set(this.filteredStructures.map((s) => s.id));
    const componentIds = new Set(
      this.components
        .filter((c) => filteredStructIds.has(c.structureId))
        .map((c) => c.id)
    );
    return this.qcInspections.filter((i) => componentIds.has(i.componentId));
  }

  get allCylinderTests(): CylinderTestRow[] {
    const rows: CylinderTestRow[] = [];
    for (const ticket of this.filteredBatchTickets) {
      for (const test of ticket.cylinderTests) {
        rows.push({ ticket, test });
      }
    }
    return rows.sort((a, b) => a.test.breakDate.localeCompare(b.test.breakDate));
  }

  get compressionTestSummary(): CompressionTestSummary {
    const rows = this.allCylinderTests;
    const by7 = rows.filter((r) => r.test.ageDays === 7);
    const by14 = rows.filter((r) => r.test.ageDays === 14);
    const by28 = rows.filter((r) => r.test.ageDays === 28);
    const avg = (xs: CylinderTestRow[]): number =>
      xs.length === 0 ? 0 : Math.round(xs.reduce((s, r) => s + r.test.actualPsi, 0) / xs.length);
    const psi28 = by28.map((r) => r.test.actualPsi);
    const passing28 = by28.filter((r) => r.test.pass).length;
    return {
      specifiedPsi: 5000,
      totalCylinders: rows.length,
      count7Day: by7.length,
      count14Day: by14.length,
      count28Day: by28.length,
      avg7Day: avg(by7),
      avg14Day: avg(by14),
      avg28Day: avg(by28),
      min28Day: psi28.length === 0 ? 0 : Math.min(...psi28),
      max28Day: psi28.length === 0 ? 0 : Math.max(...psi28),
      passRate28Day: by28.length === 0 ? 0 : passing28 / by28.length,
    };
  }

  get batchQualitySummary(): BatchQualitySummary {
    const batches = this.filteredBatchTickets;
    if (batches.length === 0) {
      return {
        totalBatches: 0,
        avgSlump: 0, slumpSpec: 4, slumpPassRate: 0,
        avgAir: 0, airSpec: 6, airPassRate: 0,
        avgTemp: 0,
      };
    }
    const slumpSum = batches.reduce((s, b) => s + b.slumpTest.measured, 0);
    const airSum = batches.reduce((s, b) => s + b.airContent.measured, 0);
    const tempSum = batches.reduce((s, b) => s + b.temperature, 0);
    const slumpPass = batches.filter((b) => b.slumpTest.pass).length;
    const airPass = batches.filter((b) => b.airContent.pass).length;
    return {
      totalBatches: batches.length,
      avgSlump: +(slumpSum / batches.length).toFixed(2),
      slumpSpec: batches[0].slumpTest.specified,
      slumpPassRate: slumpPass / batches.length,
      avgAir: +(airSum / batches.length).toFixed(2),
      airSpec: batches[0].airContent.specified,
      airPassRate: airPass / batches.length,
      avgTemp: Math.round(tempSum / batches.length),
    };
  }

  get openQcItems(): OpenQcItemRow[] {
    const today = new Date();
    const componentById = new Map(this.components.map((c) => [c.id, c]));
    const structureById = new Map(this.structures.map((s) => [s.id, s]));
    const assignmentByComponentId = new Map(
      this.moldAssignments.map((ma) => [ma.componentId, ma])
    );
    const rows: OpenQcItemRow[] = [];
    for (const inspection of this.filteredQcInspections) {
      if (inspection.status === "complete") continue;
      const comp = componentById.get(inspection.componentId);
      if (!comp) continue;
      const struct = structureById.get(comp.structureId);
      if (!struct) continue;
      const assignment = assignmentByComponentId.get(comp.id);

      const pourDateStr = comp.actualCastDate || inspection.inspectionDate;
      let daysSincePour = 0;
      if (pourDateStr) {
        const pourDate = new Date(pourDateStr + "T00:00:00");
        daysSincePour = Math.max(
          0,
          Math.floor((today.getTime() - pourDate.getTime()) / 86400000)
        );
      }

      rows.push({
        inspectionId: inspection.id,
        componentId: comp.id,
        controlNumber: comp.controlNumber,
        structureId: struct.id,
        markNumber: struct.markNumber,
        inspectionType: inspection.inspectionType,
        status: inspection.status,
        inspectionDate: inspection.inspectionDate,
        daysSincePour,
        moldAssignmentId: assignment?.id || "",
      });
    }
    return rows.sort((a, b) => b.daysSincePour - a.daysSincePour);
  }

  // --- QC test data entry (batch tickets + cylinder breaks) ---

  qcDialogPrefill: { batchTicketId?: string; cylinderId?: string } | null = null;

  openLogBatchTicketDialog = () => {
    this.qcDialogPrefill = null;
    this.rootStore.routerStore.openDialog({ dialogName: "logBatchTicket" });
  };

  openLogCylinderBreakDialog = (prefill?: { batchTicketId: string; cylinderId: string }) => {
    this.qcDialogPrefill = prefill ?? null;
    this.rootStore.routerStore.openDialog({ dialogName: "logCylinderBreak" });
  };

  /** Cylinders that have been cast but not yet broken (actualPsi === 0). */
  get pendingCylinders(): Array<{
    ticket: StructureCastBatchTicket;
    test: CylinderTestResult;
    castingDayDate: string;
  }> {
    const rows: Array<{
      ticket: StructureCastBatchTicket;
      test: CylinderTestResult;
      castingDayDate: string;
    }> = [];
    for (const ticket of this.filteredBatchTickets) {
      for (const test of ticket.cylinderTests) {
        if (test.actualPsi === 0) {
          rows.push({ ticket, test, castingDayDate: ticket.date });
        }
      }
    }
    return rows.sort((a, b) => a.test.breakDate.localeCompare(b.test.breakDate));
  }

  createBatchTicket = async (input: {
    castingDayId: string;
    concreteSupplier: string;
    mixDesign: string;
    cubicYardsDelivered: number;
    slumpMeasured: number;
    slumpSpecified: number;
    airMeasured: number;
    airSpecified: number;
    temperature: number;
    notes: string;
  }): Promise<StructureCastBatchTicket | undefined> => {
    if (!this._batchTicketStore) return undefined;
    const day = this.castingDays.find((d) => d.id === input.castingDayId);
    if (!day) return undefined;

    // Generate next ticket number from the highest existing numeric ticket
    const lastNum = this.batchTickets
      .map((t) => parseInt(t.ticketNumber, 10))
      .filter((n) => !Number.isNaN(n))
      .reduce((max, n) => (n > max ? n : max), 88400);
    const ticketNum = lastNum + 1;
    const castDate = day.actualCastDate || day.scheduledDate;

    const addDays = (iso: string, n: number): string => {
      const d = new Date(iso + "T00:00:00");
      d.setDate(d.getDate() + n);
      return d.toISOString().split("T")[0];
    };

    const ages: Array<7 | 14 | 28> = [7, 14, 28];
    const cylinderTests: CylinderTestResult[] = ages.map((age, idx) => ({
      cylinderId: `CYL-${ticketNum}-${String.fromCharCode(65 + idx)}`,
      ageDays: age,
      breakDate: addDays(castDate, age),
      specifiedPsi: 5000,
      actualPsi: 0, // pending — will be filled in via Log Cylinder Break
      pass: false,
      technician: "",
    }));

    const slumpPass = input.slumpMeasured >= 3 && input.slumpMeasured <= 6;
    const airPass = input.airMeasured >= 4 && input.airMeasured <= 8;

    const created = await this._batchTicketStore.create({
      castingDayId: input.castingDayId,
      ticketNumber: String(ticketNum),
      date: castDate,
      concreteSupplier: input.concreteSupplier,
      mixDesign: input.mixDesign,
      cubicYardsDelivered: input.cubicYardsDelivered,
      slumpTest: {
        specified: input.slumpSpecified,
        measured: input.slumpMeasured,
        pass: slumpPass,
      },
      airContent: {
        specified: input.airSpecified,
        measured: input.airMeasured,
        pass: airPass,
      },
      temperature: input.temperature,
      cylinderTests,
      notes: input.notes,
    });
    return created;
  };

  recordCylinderBreak = async (
    batchTicketId: string,
    cylinderId: string,
    actualPsi: number,
    technician: string
  ): Promise<void> => {
    if (!this._batchTicketStore) return;
    const ticket = this.batchTickets.find((t) => t.id === batchTicketId);
    if (!ticket) return;
    const updatedTests = ticket.cylinderTests.map((t) => {
      if (t.cylinderId !== cylinderId) return t;
      const pass =
        t.ageDays === 28
          ? actualPsi >= t.specifiedPsi
          : actualPsi >= t.specifiedPsi * (t.ageDays === 7 ? 0.65 : 0.85);
      return { ...t, actualPsi, pass, technician };
    });
    await this._batchTicketStore.patch(batchTicketId, { cylinderTests: updatedTests });
  };

  toggleQcCheckItem = async (inspectionId: string, sectionNumber: number, itemId: string) => {
    if (!this._qcInspectionStore) return;
    const inspection = this.qcInspections.find((i) => i.id === inspectionId);
    if (!inspection) return;

    const updatedSections = inspection.sections.map((section) => {
      if (section.sectionNumber !== sectionNumber) return section;
      return {
        ...section,
        items: section.items.map((item) =>
          item.itemId === itemId ? { ...item, checked: !item.checked } : item
        ),
      };
    });

    // Determine new status
    const allChecked = updatedSections.every((s) => s.items.every((i) => i.checked));
    const anyChecked = updatedSections.some((s) => s.items.some((i) => i.checked));
    const status = allChecked ? "complete" : anyChecked ? "in_progress" : "not_started";

    await this._qcInspectionStore.patch(inspectionId, {
      sections: updatedSections,
      status,
      updatedAt: new Date().toISOString(),
    });
  };

  updateQcCheckItemNotes = async (inspectionId: string, sectionNumber: number, itemId: string, notes: string) => {
    if (!this._qcInspectionStore) return;
    const inspection = this.qcInspections.find((i) => i.id === inspectionId);
    if (!inspection) return;

    const updatedSections = inspection.sections.map((section) => {
      if (section.sectionNumber !== sectionNumber) return section;
      return {
        ...section,
        items: section.items.map((item) =>
          item.itemId === itemId ? { ...item, notes } : item
        ),
      };
    });

    await this._qcInspectionStore.patch(inspectionId, {
      sections: updatedSections,
      updatedAt: new Date().toISOString(),
    });
  };

  updateQcCheckItemMeasurement = async (inspectionId: string, sectionNumber: number, itemId: string, measured: string) => {
    if (!this._qcInspectionStore) return;
    const inspection = this.qcInspections.find((i) => i.id === inspectionId);
    if (!inspection) return;

    const updatedSections = inspection.sections.map((section) => {
      if (section.sectionNumber !== sectionNumber) return section;
      return {
        ...section,
        items: section.items.map((item) => {
          if (item.itemId !== itemId) return item;
          const tolerance = item.specified ? Math.abs(parseFloat(measured) - parseFloat(item.specified)) <= 0.5 : true;
          return { ...item, measured, withinTolerance: tolerance ? "Yes" : "No" };
        }),
      };
    });

    await this._qcInspectionStore.patch(inspectionId, {
      sections: updatedSections,
      updatedAt: new Date().toISOString(),
    });
  };

  createQcInspection = async (componentId: string, inspectionType: "pre_pour" | "post_pour") => {
    if (!this._qcInspectionStore) return;
    const comp = this.getComponent(componentId);
    if (!comp) return;

    const struct = this.getStructure(comp.structureId);
    const structIdx = struct ? parseInt(struct.id.replace("sc-struct-", ""), 10) - 1 : 0;
    let productType: StructureCastQcInspection["productType"] = "storm_box";
    if (structIdx <= 3) productType = "storm_box";
    else if (structIdx <= 7) productType = "manhole_48";
    else if (structIdx <= 11) productType = "pullbox";
    else if (structIdx <= 13) productType = "cable_vault";
    else productType = "manhole_rect";

    const size = `${comp.length}" x ${comp.width}"`;
    const compHeightIn = comp.heightFeet * 12 + comp.heightInches;
    const wallThickness = comp.componentType === "base" ? 6 : comp.componentType === "riser" ? 5 : 4;

    const now = new Date().toISOString();
    // Import template sections inline — ASTM C478 standards
    const sections = inspectionType === "pre_pour"
      ? [
          { sectionNumber: 1, sectionName: "Form & Insert Preparation", items: [
            { itemId: "pp-1-1", label: "Form cleaned and free of debris", checked: false, notes: "" },
            { itemId: "pp-1-2", label: "Release agent applied per spec", checked: false, notes: "" },
            { itemId: "pp-1-3", label: "Form properly secured and aligned", checked: false, notes: "" },
            { itemId: "pp-1-4", label: "Knockout forms installed per drawings", checked: false, notes: "" },
            { itemId: "pp-1-5", label: "Pull irons (1/2\" inserts) positioned at 12\" spacing", checked: false, notes: "" },
            { itemId: "pp-1-6", label: "2-ton RISS handling anchors positioned per drawings", checked: false, notes: "" },
          ]},
          { sectionNumber: 2, sectionName: "Reinforcement Checks", items: [
            { itemId: "pp-2-1", label: "ASTM A-615 Grade 60 rebar placed per drawings", checked: false, notes: "" },
            { itemId: "pp-2-2", label: "Rebar clearance and cover adequate (min 1\" clear)", checked: false, notes: "" },
            { itemId: "pp-2-3", label: "Tie wires secure and trimmed", checked: false, notes: "" },
          ]},
          { sectionNumber: 3, sectionName: "Additional Items", items: [
            { itemId: "pp-3-1", label: "Knockout positions verified per drawing", checked: false, notes: "" },
            { itemId: "pp-3-2", label: "Handling anchor locations confirmed", checked: false, notes: "" },
            { itemId: "pp-3-3", label: "Dimensions double-checked against shop drawings", checked: false, notes: "" },
          ]},
        ]
      : [
          { sectionNumber: 1, sectionName: "Visual Inspection", items: [
            { itemId: "po-1-1", label: "Surface finish — no excessive bugholes or voids", checked: false, notes: "" },
            { itemId: "po-1-2", label: "Corners and edges intact — no chips or cracks", checked: false, notes: "" },
            { itemId: "po-1-3", label: "Handling anchors accessible and undamaged", checked: false, notes: "" },
            { itemId: "po-1-4", label: "Mark number and structure ID legible", checked: false, notes: "" },
            { itemId: "po-1-5", label: "Knockouts clean and properly formed", checked: false, notes: "" },
          ]},
          { sectionNumber: 2, sectionName: "Dimensional Check", items: [
            { itemId: "po-2-1", label: "Length/Diameter", checked: false, notes: "", specified: `${comp.length}"`, measured: "", withinTolerance: "" },
            { itemId: "po-2-2", label: "Width", checked: false, notes: "", specified: `${comp.width}"`, measured: "", withinTolerance: "" },
            { itemId: "po-2-3", label: "Height", checked: false, notes: "", specified: `${compHeightIn}"`, measured: "", withinTolerance: "" },
            { itemId: "po-2-4", label: "Wall Thickness", checked: false, notes: "", specified: `${wallThickness}"`, measured: "", withinTolerance: "" },
          ]},
          { sectionNumber: 3, sectionName: "Embedded Items Check", items: [
            { itemId: "po-3-1", label: "Pull irons properly positioned at 12\" spacing", checked: false, notes: "" },
            { itemId: "po-3-2", label: "Knockouts clear and sealed", checked: false, notes: "" },
            { itemId: "po-3-3", label: "RISS handling anchors secure", checked: false, notes: "" },
          ]},
          { sectionNumber: 4, sectionName: "Pour Inventory", items: [
            { itemId: "po-4-1", label: "Pour inventory sent to office", checked: false, notes: "" },
          ]},
        ];

    await this._qcInspectionStore.create({
      id: `sc-qc-new-${Date.now()}`,
      componentId,
      inspectionType,
      inspector: "",
      inspectionDate: now.split("T")[0],
      productType,
      size,
      projectJobNumber: "25-1585",
      status: "not_started",
      sections,
      createdAt: now,
      updatedAt: now,
    });
  };

  // --- Computed: filtered by project ---

  get filteredStructures(): StructureCastStructure[] {
    if (!this.selectedProjectId) return this.structures;
    return this.structures.filter((s) => s.projectId === this.selectedProjectId);
  }

  get filteredCastingGroups(): StructureCastCastingGroup[] {
    let groups = this.castingGroups;
    if (this.selectedProjectId) {
      groups = groups.filter((g) => g.projectId === this.selectedProjectId);
    }
    if (this.selectedCastingGroupId) {
      groups = groups.filter((g) => g.id === this.selectedCastingGroupId);
    }
    return groups.sort((a, b) => a.groupNumber - b.groupNumber);
  }

  get filteredCastingDays(): StructureCastCastingDay[] {
    const groupIds = new Set(this.filteredCastingGroups.map((g) => g.id));
    return this.castingDays
      .filter((d) => groupIds.has(d.castingGroupId))
      .sort((a, b) => a.scheduledDate.localeCompare(b.scheduledDate));
  }

  get castingDayDates(): string[] {
    const dates = new Set<string>();
    for (const day of this.filteredCastingDays) {
      if (day.scheduledDate) dates.add(day.scheduledDate);
    }
    return Array.from(dates).sort();
  }

  getAssignmentsForDate = (date: string): StructureCastMoldAssignment[] => {
    const dayIds = new Set(
      this.castingDays
        .filter((d) => d.scheduledDate === date)
        .map((d) => d.id)
    );
    return this.moldAssignments.filter((ma) => dayIds.has(ma.castingDayId));
  };

  getTableAssignmentsForDate = (tableId: string, date: string): StructureCastMoldAssignment[] => {
    return this.getAssignmentsForDate(date).filter(
      (ma) => ma.castingTableId === tableId
    );
  };

  get filteredShipments(): StructureCastShipment[] {
    if (!this.selectedProjectId) return this.shipments;
    return this.shipments.filter((s) => s.projectId === this.selectedProjectId);
  }

  get filteredPreProductionMeetings(): StructureCastPreProductionMeeting[] {
    if (!this.selectedProjectId) return this.preProductionMeetings;
    return this.preProductionMeetings.filter((m) => m.projectId === this.selectedProjectId);
  }

  get filteredEstimates(): StructureCastEstimate[] {
    if (!this.selectedProjectId) return this.estimates;
    return this.estimates.filter((e) => e.projectId === this.selectedProjectId);
  }

  getEstimateLineItems = (estimateId: string): StructureCastEstimateLineItem[] => {
    return this.estimateLineItems.filter((li) => li.estimateId === estimateId);
  };

  getEstimate = (estimateId: string): StructureCastEstimate | undefined => {
    return this.estimates.find((e) => e.id === estimateId);
  };

  // --- Computed: Casting Schedule Grid ---

  get castingGrid(): Map<string, CastingGridRow[]> {
    const grid = new Map<string, CastingGridRow[]>();

    for (const group of this.filteredCastingGroups) {
      const days = this.castingDays
        .filter((d) => d.castingGroupId === group.id)
        .sort((a, b) => a.dayNumber - b.dayNumber);

      const rows: CastingGridRow[] = days.map((day) => {
        const assignments = this.moldAssignments.filter(
          (ma) => ma.castingDayId === day.id
        );

        const moldPositions = new Map<number | "sump", StructureCastMoldAssignment[]>();
        for (let pos = 1; pos <= 5; pos++) {
          moldPositions.set(pos, assignments.filter((a) => a.moldPosition === pos));
        }
        moldPositions.set("sump", assignments.filter((a) => a.moldPosition === "sump"));

        const totalCY = assignments.reduce((sum, a) => sum + a.cubicYards, 0);

        return { castingDay: day, moldPositions, totalCubicYards: totalCY };
      });

      grid.set(group.id, rows);
    }

    return grid;
  }

  // --- Computed: Unified Casting Grid (continuous weekday calendar) ---

  get unifiedCastingGrid(): UnifiedCastingGridRow[] {
    // Build lookup: date string -> { day, group, assignments }
    const daysByDate = new Map<
      string,
      { day: StructureCastCastingDay; group: StructureCastCastingGroup }
    >();

    for (const group of this.filteredCastingGroups) {
      const days = this.castingDays.filter((d) => d.castingGroupId === group.id);
      for (const day of days) {
        daysByDate.set(day.scheduledDate, { day, group });
      }
    }

    // Generate every weekday in the date range
    const rows: UnifiedCastingGridRow[] = [];
    const start = new Date(this.dateRangeStart + "T00:00:00");
    const end = new Date(this.dateRangeEnd + "T00:00:00");
    const seenGroups = new Set<string>();

    for (
      const cursor = new Date(start);
      cursor <= end;
      cursor.setDate(cursor.getDate() + 1)
    ) {
      const dayOfWeek = cursor.getDay();
      if (dayOfWeek === 0 || dayOfWeek === 6) continue; // skip weekends

      const dateStr =
        cursor.getFullYear() +
        "-" +
        String(cursor.getMonth() + 1).padStart(2, "0") +
        "-" +
        String(cursor.getDate()).padStart(2, "0");

      const entry = daysByDate.get(dateStr);

      if (entry) {
        const { day, group } = entry;
        const assignments = this.moldAssignments.filter(
          (ma) => ma.castingDayId === day.id
        );

        const moldPositions = new Map<number | "sump", StructureCastMoldAssignment[]>();
        for (let pos = 1; pos <= 5; pos++) {
          moldPositions.set(pos, assignments.filter((a) => a.moldPosition === pos));
        }
        moldPositions.set("sump", assignments.filter((a) => a.moldPosition === "sump"));

        const totalCY = assignments.reduce((sum, a) => sum + a.cubicYards, 0);
        const isFirst = !seenGroups.has(group.id);
        if (isFirst) seenGroups.add(group.id);

        rows.push({
          date: dateStr,
          castingDay: day,
          group,
          moldPositions,
          totalCubicYards: totalCY,
          isFirstDayOfGroup: isFirst,
        });
      } else {
        // Empty calendar day — no casting scheduled
        rows.push({
          date: dateStr,
          castingDay: null,
          group: null,
          moldPositions: new Map(),
          totalCubicYards: 0,
          isFirstDayOfGroup: false,
        });
      }
    }

    return rows;
  }

  // --- Computed: CY by day ---

  get cubicYardsByDay(): Map<string, number> {
    const result = new Map<string, number>();
    for (const day of this.filteredCastingDays) {
      const assignments = this.moldAssignments.filter((ma) => ma.castingDayId === day.id);
      const totalCY = assignments.reduce((sum, a) => sum + a.cubicYards, 0);
      result.set(day.id, totalCY);
    }
    return result;
  }

  // --- Computed: Structures with completion status ---

  get structureCompletionStatus(): StructureCompletionRow[] {
    return this.filteredStructures.map((struct) => {
      const comps = this.components
        .filter((c) => c.structureId === struct.id)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      const castCount = comps.filter(
        (c) => c.status === "cast" || c.status === "cured" || c.status === "shipped"
      ).length;

      return {
        structureId: struct.id,
        markNumber: struct.markNumber,
        stationReference: struct.stationReference,
        overallLength: struct.overallLength,
        overallWidth: struct.overallWidth,
        overallHeight: struct.overallHeight,
        totalWeight: struct.totalWeight,
        totalCubicYards: struct.totalCubicYards,
        status: struct.status,
        totalComponents: comps.length,
        castComponents: castCount,
        components: comps,
      };
    });
  }

  // --- Computed: Unscheduled components ---

  get unscheduledComponents(): UnscheduledComponentRow[] {
    const scheduledComponentIds = new Set(
      this.moldAssignments.map((ma) => ma.componentId)
    );

    return this.components
      .filter((c) => {
        if (this.selectedProjectId) {
          const struct = this.structures.find((s) => s.id === c.structureId);
          if (struct?.projectId !== this.selectedProjectId) return false;
        }
        return !scheduledComponentIds.has(c.id) && c.status === "pending";
      })
      .map((c) => {
        const struct = this.structures.find((s) => s.id === c.structureId);
        return {
          componentId: c.id,
          structureId: c.structureId,
          markNumber: struct?.markNumber || "",
          controlNumber: c.controlNumber,
          componentType: c.componentType,
          weight: c.weight,
          cubicYards: c.cubicYards,
          stationReference: struct?.stationReference || "",
        };
      });
  }

  // --- Computed: Group progress ---

  get groupProgress(): GroupProgress[] {
    return this.filteredCastingGroups.map((group) => {
      const days = this.castingDays.filter((d) => d.castingGroupId === group.id);
      const completedDays = days.filter((d) => d.status === "completed").length;

      const assignments = this.moldAssignments.filter((ma) =>
        days.some((d) => d.id === ma.castingDayId)
      );
      const completedAssignments = assignments.filter(
        (a) => a.status === "cured" || a.status === "stripped"
      ).length;

      return {
        groupId: group.id,
        groupName: group.name,
        groupNumber: group.groupNumber,
        status: group.status,
        totalDays: days.length,
        completedDays,
        totalAssignments: assignments.length,
        completedAssignments,
        progressPercent:
          assignments.length > 0
            ? Math.round((completedAssignments / assignments.length) * 100)
            : 0,
      };
    });
  }

  // --- Metrics tab aggregations ---

  get productionVelocity(): {
    totalCYCast: number;
    totalWeightCast: number;
    avgCYPerCompletedDay: number;
    peakCYDay: number;
    completedDayCount: number;
    targetCapacity: number;
    velocityRatio: number; // avgCYPerCompletedDay / targetCapacity
  } {
    const targetCapacity = 4.5;
    // Count "production" toward velocity from completed casting days only.
    const completedDays = this.filteredCastingDays.filter((d) => d.status === "completed");
    const cyByDay = this.cubicYardsByDay;

    let totalCYCast = 0;
    let totalWeightCast = 0;
    let peakCYDay = 0;
    const completedDayIds = new Set(completedDays.map((d) => d.id));

    for (const day of completedDays) {
      const cy = cyByDay.get(day.id) || 0;
      totalCYCast += cy;
      if (cy > peakCYDay) peakCYDay = cy;
    }

    for (const ma of this.moldAssignments) {
      if (completedDayIds.has(ma.castingDayId) && (ma.status === "cured" || ma.status === "stripped" || ma.status === "poured")) {
        totalWeightCast += ma.weight;
      }
    }

    const avgCYPerCompletedDay = completedDays.length > 0 ? totalCYCast / completedDays.length : 0;

    return {
      totalCYCast: +totalCYCast.toFixed(2),
      totalWeightCast,
      avgCYPerCompletedDay: +avgCYPerCompletedDay.toFixed(2),
      peakCYDay: +peakCYDay.toFixed(2),
      completedDayCount: completedDays.length,
      targetCapacity,
      velocityRatio: targetCapacity > 0 ? +(avgCYPerCompletedDay / targetCapacity).toFixed(2) : 0,
    };
  }

  get componentMixSummary(): Array<{
    type: "base" | "riser" | "cone" | "flat_top";
    label: string;
    total: number;
    pending: number;
    scheduled: number;
    cast: number;
    cured: number;
    shipped: number;
    totalCY: number;
  }> {
    const labelMap: Record<string, string> = {
      base: "Bases",
      riser: "Shafts / Risers",
      cone: "Cones",
      flat_top: "Grade Rings",
    };
    const filteredStructIds = new Set(this.filteredStructures.map((s) => s.id));
    const projectComps = this.components.filter((c) => filteredStructIds.has(c.structureId));

    const order: Array<"base" | "riser" | "cone" | "flat_top"> = ["base", "riser", "cone", "flat_top"];
    return order.map((type) => {
      const sub = projectComps.filter((c) => c.componentType === type);
      const totalCY = sub.reduce((s, c) => s + c.cubicYards, 0);
      const buckets = {
        pending: 0,
        scheduled: 0,
        cast: 0,
        cured: 0,
        shipped: 0,
      };
      for (const c of sub) {
        if (c.status in buckets) {
          (buckets as Record<string, number>)[c.status] += 1;
        }
      }
      return {
        type,
        label: labelMap[type],
        total: sub.length,
        pending: buckets.pending,
        scheduled: buckets.scheduled,
        cast: buckets.cast,
        cured: buckets.cured,
        shipped: buckets.shipped,
        totalCY: +totalCY.toFixed(2),
      };
    });
  }

  get moldUtilization(): Array<{
    position: number | "sump";
    label: string;
    scheduledCount: number;
    completedCount: number;
    totalCY: number;
    totalWeight: number;
  }> {
    const filteredStructIds = new Set(this.filteredStructures.map((s) => s.id));
    const componentToStruct = new Map<string, string>();
    for (const c of this.components) componentToStruct.set(c.id, c.structureId);

    const rows: Array<{
      position: number | "sump";
      label: string;
      scheduledCount: number;
      completedCount: number;
      totalCY: number;
      totalWeight: number;
    }> = [];

    const positions: Array<{ key: number | "sump"; label: string }> = [
      { key: 1, label: "Mold 1" },
      { key: 2, label: "Mold 2" },
      { key: 3, label: "Mold 3" },
      { key: 4, label: "Mold 4" },
      { key: 5, label: "Mold 5" },
      { key: "sump", label: "Grade Ring" },
    ];

    for (const pos of positions) {
      const sub = this.moldAssignments.filter((ma) => {
        if (ma.moldPosition !== pos.key) return false;
        const structId = componentToStruct.get(ma.componentId);
        return structId ? filteredStructIds.has(structId) : false;
      });
      const completed = sub.filter((ma) => ma.status === "cured" || ma.status === "stripped").length;
      const cy = sub.reduce((s, ma) => s + ma.cubicYards, 0);
      const wt = sub.reduce((s, ma) => s + ma.weight, 0);
      rows.push({
        position: pos.key,
        label: pos.label,
        scheduledCount: sub.length,
        completedCount: completed,
        totalCY: +cy.toFixed(2),
        totalWeight: wt,
      });
    }
    return rows;
  }

  // --- Summary metrics ---

  get totalScheduledComponents(): number {
    return this.moldAssignments.length;
  }

  get totalCubicYards(): number {
    return +this.moldAssignments
      .reduce((sum, ma) => sum + ma.cubicYards, 0)
      .toFixed(2);
  }

  get totalStructures(): number {
    return this.filteredStructures.length;
  }

  get completedStructures(): number {
    return this.filteredStructures.filter(
      (s) => s.status === "cast_complete" || s.status === "shipped"
    ).length;
  }

  get remainingPieceCount(): number {
    const filtered = this.selectedProjectId
      ? this.components.filter((c) => {
          const struct = this.structures.find((s) => s.id === c.structureId);
          return struct?.projectId === this.selectedProjectId;
        })
      : this.components;
    return filtered.filter(
      (c) => c.status !== "cast" && c.status !== "cured" && c.status !== "shipped"
    ).length;
  }

  // --- Lookup helpers ---

  getStructure = (structureId: string): StructureCastStructure | undefined => {
    return this.structures.find((s) => s.id === structureId);
  };

  getComponent = (componentId: string): StructureCastComponent | undefined => {
    return this.components.find((c) => c.id === componentId);
  };

  getCastingGroup = (groupId: string): StructureCastCastingGroup | undefined => {
    return this.castingGroups.find((g) => g.id === groupId);
  };

  getLoadsForShipment = (shipmentId: string): StructureCastShipmentLoad[] => {
    return this.shipmentLoads.filter((l) => l.shipmentId === shipmentId);
  };

  getCastingTable = (tableId: string): StructureCastCastingTable | undefined => {
    return this.castingTables.find((t) => t.id === tableId);
  };

  getTableAssignments = (tableId: string): StructureCastMoldAssignment[] => {
    return this.moldAssignments.filter((ma) => ma.castingTableId === tableId);
  };

  getYardZoneAssignments = (zoneId: string): StructureCastMoldAssignment[] => {
    return this.moldAssignments.filter((ma) => ma.yardZoneId === zoneId);
  };

  getShipmentLoadAssignments = (loadId: string): StructureCastMoldAssignment[] => {
    return this.moldAssignments.filter((ma) => ma.shipmentLoadId === loadId);
  };

  getYardZone = (zoneId: string): StructureCastYardZone | undefined => {
    return this.yardZones.find((z) => z.id === zoneId);
  };

  // Produced pieces (poured/stripped/cured) not yet assigned to a yard zone
  get producedUnstored(): StructureCastMoldAssignment[] {
    return this.moldAssignments.filter(
      (ma) =>
        (ma.status === "poured" || ma.status === "stripped" || ma.status === "cured") &&
        !ma.yardZoneId
    );
  }

  // Cured pieces in a yard zone but not yet assigned to a shipment load
  get readyToShip(): StructureCastMoldAssignment[] {
    return this.moldAssignments.filter(
      (ma) =>
        ma.status === "cured" &&
        !!ma.yardZoneId &&
        !ma.shipmentLoadId
    );
  }

  // --- Actions ---

  // Update actual cast date on a component (no schedule move)
  updateComponentActualDate = async (componentId: string, actualCastDate: string) => {
    if (!this._componentStore) return;
    await this._componentStore.patch(componentId, {
      actualCastDate,
      updatedAt: new Date().toISOString(),
    });
  };

  // Move a piece to a different scheduled date — finds or creates a casting day
  // on the target date, moves the mold assignment, and updates the component.
  rescheduleComponent = async (componentId: string, newDate: string) => {
    if (!this._componentStore || !this._castingDayStore || !this._moldAssignmentStore) return;

    const comp = this.getComponent(componentId);
    if (!comp) return;

    // Find the mold assignment for this component
    const assignment = this.moldAssignments.find((ma) => ma.componentId === componentId);
    if (!assignment) {
      // No mold assignment — just update the component date
      await this._componentStore.patch(componentId, {
        scheduledDate: newDate,
        updatedAt: new Date().toISOString(),
      });
      return;
    }

    // Current casting day (to know the group)
    const currentDay = this.castingDays.find((d) => d.id === assignment.castingDayId);
    const groupId = currentDay?.castingGroupId || comp.castingGroupId;

    // Look for an existing casting day on newDate within the same group
    let targetDay = this.castingDays.find(
      (d) => d.castingGroupId === groupId && d.scheduledDate === newDate
    );

    // If none exists, create one
    if (!targetDay) {
      const groupDays = this.castingDays.filter((d) => d.castingGroupId === groupId);
      const nextDayNumber = groupDays.length > 0
        ? Math.max(...groupDays.map((d) => d.dayNumber)) + 1
        : 1;

      const now = new Date().toISOString();
      const newDay = {
        id: `sc-cd-resc-${Date.now()}`,
        castingGroupId: groupId,
        dayNumber: nextDayNumber,
        scheduledDate: newDate,
        actualCastDate: "",
        totalCubicYards: 0,
        status: "planned" as const,
        notes: "",
        createdAt: now,
        updatedAt: now,
      };
      await this._castingDayStore.create(newDay);

      // Re-fetch after creation
      targetDay = this.castingDays.find(
        (d) => d.castingGroupId === groupId && d.scheduledDate === newDate
      );
    }

    if (!targetDay) return; // safety

    // Move the mold assignment to the new day
    await this._moldAssignmentStore.patch(assignment.id, {
      castingDayId: targetDay.id,
      updatedAt: new Date().toISOString(),
    });

    // Update the component
    await this._componentStore.patch(componentId, {
      scheduledDate: newDate,
      castingDayId: targetDay.id,
      updatedAt: new Date().toISOString(),
    });
  };

  /** Move a mold assignment to a different casting day and/or mold position */
  /**
   * Add `offset` business days to `dateStr` (skipping Sat/Sun).
   * Negative offset pulls backward.
   */
  private _addBusinessDays = (dateStr: string, offset: number): string => {
    if (!dateStr) return dateStr;
    const date = new Date(dateStr + (dateStr.includes("T") ? "" : "T00:00:00"));
    let remaining = Math.abs(offset);
    const direction = offset >= 0 ? 1 : -1;
    while (remaining > 0) {
      date.setDate(date.getDate() + direction);
      const dow = date.getDay();
      if (dow !== 0 && dow !== 6) remaining--;
    }
    return date.toISOString().split("T")[0];
  };

  /**
   * Preview the impact of pushing pieces in the given mold position columns
   * by `offsetBusinessDays`. Returns the number of pieces that would move and
   * a list of per-piece old → new dates. Does not mutate anything.
   *
   * Only "planned" mold assignments are eligible — already-poured pieces stay put.
   */
  previewPushByMoldPosition = (
    moldPositions: Array<1 | 2 | 3 | 4 | 5 | "sump">,
    offsetBusinessDays: number
  ): {
    affected: number;
    eligibleTotal: number;
    moves: Array<{ markNumber: string; controlNumber: string; oldDate: string; newDate: string; moldPosition: number | "sump" }>;
  } => {
    if (moldPositions.length === 0 || offsetBusinessDays === 0) {
      return { affected: 0, eligibleTotal: 0, moves: [] };
    }
    const moldSet = new Set<number | "sump">(moldPositions);
    const eligible = this.moldAssignments.filter(
      (ma) => ma.status === "planned" && moldSet.has(ma.moldPosition)
    );
    const allEligible = this.moldAssignments.filter((ma) => ma.status === "planned");
    const moves = eligible
      .map((ma) => {
        const day = this.castingDays.find((d) => d.id === ma.castingDayId);
        if (!day) return null;
        const comp = this.getComponent(ma.componentId);
        const struct = comp ? this.getStructure(comp.structureId) : undefined;
        return {
          markNumber: struct?.markNumber ?? "?",
          controlNumber: comp?.controlNumber ?? "?",
          oldDate: day.scheduledDate,
          newDate: this._addBusinessDays(day.scheduledDate, offsetBusinessDays),
          moldPosition: ma.moldPosition,
        };
      })
      .filter((m): m is NonNullable<typeof m> => m !== null);
    return { affected: eligible.length, eligibleTotal: allEligible.length, moves };
  };

  /**
   * Mass-push: shift every planned piece in the given mold position columns
   * forward (or backward) by `offsetBusinessDays`. Pieces stay in the same
   * mold position; they move to a casting day in the same group on the new
   * date. New casting days are created as needed.
   */
  pushPiecesByMoldPosition = async (
    moldPositions: Array<1 | 2 | 3 | 4 | 5 | "sump">,
    offsetBusinessDays: number
  ): Promise<{ piecesMoved: number; daysCreated: number }> => {
    if (
      !this._castingDayStore ||
      moldPositions.length === 0 ||
      offsetBusinessDays === 0
    ) {
      return { piecesMoved: 0, daysCreated: 0 };
    }
    const moldSet = new Set<number | "sump">(moldPositions);
    const eligible = this.moldAssignments
      .filter((ma) => ma.status === "planned" && moldSet.has(ma.moldPosition))
      .slice(); // snapshot — we'll mutate during iteration

    let daysCreated = 0;
    let piecesMoved = 0;

    for (const ma of eligible) {
      const oldDay = this.castingDays.find((d) => d.id === ma.castingDayId);
      if (!oldDay) continue;
      const newDate = this._addBusinessDays(oldDay.scheduledDate, offsetBusinessDays);
      if (newDate === oldDay.scheduledDate) continue;

      // Find an existing planned day in the same group on the target date,
      // or create a new one.
      let targetDay = this.castingDays.find(
        (d) =>
          d.castingGroupId === oldDay.castingGroupId &&
          d.scheduledDate === newDate
      );

      if (!targetDay) {
        const existingCount = this.castingDays.filter(
          (d) => d.castingGroupId === oldDay.castingGroupId
        ).length;
        const now = new Date().toISOString();
        const created = await this._castingDayStore.create({
          castingGroupId: oldDay.castingGroupId,
          dayNumber: existingCount + 1,
          scheduledDate: newDate,
          actualCastDate: "",
          totalCubicYards: 0,
          status: "planned" as const,
          notes: `Auto-created by push (mold ${String(ma.moldPosition)} → ${offsetBusinessDays > 0 ? "+" : ""}${offsetBusinessDays} business days)`,
          createdAt: now,
          updatedAt: now,
        });
        targetDay = created;
        daysCreated++;
      }

      if (!targetDay) continue;

      await this.reassignMoldAssignment(ma.id, targetDay.id, ma.moldPosition as 1 | 2 | 3 | 4 | 5 | "sump");
      piecesMoved++;
    }

    return { piecesMoved, daysCreated };
  };

  /**
   * Find the casting group that best fits a given date. Picks the group of
   * the nearest existing casting day (within the active project filter),
   * falling back to the first filtered group.
   */
  private _findNearestGroupForDate = (dateStr: string): string | undefined => {
    if (!dateStr) return this.filteredCastingGroups[0]?.id;
    const targetTime = new Date(dateStr + "T00:00:00").getTime();
    const filteredGroupIds = new Set(this.filteredCastingGroups.map((g) => g.id));
    let bestGroupId: string | undefined;
    let bestDiff = Infinity;
    for (const d of this.castingDays) {
      if (!filteredGroupIds.has(d.castingGroupId)) continue;
      const diff = Math.abs(new Date(d.scheduledDate + "T00:00:00").getTime() - targetTime);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestGroupId = d.castingGroupId;
      }
    }
    return bestGroupId ?? this.filteredCastingGroups[0]?.id;
  };

  /**
   * Return an existing planned casting day at `dateStr`, or create a new one in
   * the nearest-fitting group.
   */
  ensureCastingDayForDate = async (
    dateStr: string
  ): Promise<StructureCastCastingDay | undefined> => {
    const existing = this.castingDays.find((d) => d.scheduledDate === dateStr);
    if (existing) return existing;

    const groupId = this._findNearestGroupForDate(dateStr);
    if (!groupId || !this._castingDayStore) return undefined;

    const existingCount = this.castingDays.filter((d) => d.castingGroupId === groupId).length;
    const now = new Date().toISOString();
    const created = await this._castingDayStore.create({
      castingGroupId: groupId,
      dayNumber: existingCount + 1,
      scheduledDate: dateStr,
      actualCastDate: "",
      totalCubicYards: 0,
      status: "planned" as const,
      notes: "Auto-created from schedule grid drop",
      createdAt: now,
      updatedAt: now,
    });
    return created;
  };

  /**
   * Drop-on-empty-day helper for the schedule grid: find or create a casting
   * day for `dateStr`, then reassign the given mold assignment to it.
   */
  reassignMoldAssignmentToDate = async (
    assignmentId: string,
    dateStr: string,
    targetMoldPosition: 1 | 2 | 3 | 4 | 5 | "sump"
  ): Promise<void> => {
    const day = await this.ensureCastingDayForDate(dateStr);
    if (!day) return;
    await this.reassignMoldAssignment(assignmentId, day.id, targetMoldPosition);
  };

  reassignMoldAssignment = async (
    assignmentId: string,
    targetDayId: string,
    targetMoldPosition: 1 | 2 | 3 | 4 | 5 | "sump"
  ) => {
    if (!this._moldAssignmentStore || !this._componentStore) return;

    const assignment = this.moldAssignments.find((ma) => ma.id === assignmentId);
    if (!assignment) return;

    const now = new Date().toISOString();

    // Update the mold assignment
    await this._moldAssignmentStore.patch(assignmentId, {
      castingDayId: targetDayId,
      moldPosition: targetMoldPosition,
      updatedAt: now,
    });

    // Update the component to match
    const targetDay = this.castingDays.find((d) => d.id === targetDayId);
    if (targetDay) {
      await this._componentStore.patch(assignment.componentId, {
        castingDayId: targetDayId,
        scheduledDate: targetDay.scheduledDate,
        moldPosition: targetMoldPosition,
        updatedAt: now,
      });
    }
  };

  setCurrentTab = (tab: number) => {
    this.currentTab = tab;
  };

  setSelectedProjectId = (projectId: string | null) => {
    this.selectedProjectId = projectId;
  };

  setSelectedCastingGroupId = (groupId: string | null) => {
    this.selectedCastingGroupId = groupId;
  };

  setDateRangeStart = (date: string) => {
    this.dateRangeStart = date;
  };

  setDateRangeEnd = (date: string) => {
    this.dateRangeEnd = date;
  };

  setSearchQuery = (query: string) => {
    this.searchQuery = query;
  };

  setEfficiencyPeriod = (period: "daily" | "weekly" | "monthly") => {
    this.efficiencyPeriod = period;
  };

  setFloorPlanDate = (date: string) => {
    this.floorPlanDate = date;
  };

  // Schedule a component to a mold position on a casting day
  assignComponentToMold = async (
    componentId: string,
    castingDayId: string,
    moldPosition: 1 | 2 | 3 | 4 | 5 | "sump"
  ) => {
    const component = this.getComponent(componentId);
    if (!component) return;

    const now = new Date().toISOString();
    const newAssignment = {
      id: `sc-ma-new-${Date.now()}`,
      castingDayId,
      componentId,
      moldPosition,
      cubicYards: component.cubicYards,
      weight: component.weight,
      status: "planned" as const,
      castingTableId: "",
      yardZoneId: "",
      shipmentLoadId: "",
      createdAt: now,
      updatedAt: now,
    };

    if (this._moldAssignmentStore) {
      await this._moldAssignmentStore.create(newAssignment);
    }

    // Update component status to scheduled
    if (this._componentStore) {
      await this._componentStore.patch(componentId, {
        status: "scheduled",
        castingDayId,
        moldPosition,
        updatedAt: now,
      });
    }

    this.dialogPrefill = null;
  };

  // Create a new casting day in a group
  createCastingDay = async (castingGroupId: string, date: string) => {
    const group = this.getCastingGroup(castingGroupId);
    if (!group) return;

    const existingDays = this.castingDays.filter(
      (d) => d.castingGroupId === castingGroupId
    );
    const dayNumber = existingDays.length + 1;

    const now = new Date().toISOString();
    const newDay = {
      id: `sc-cd-new-${Date.now()}`,
      castingGroupId,
      dayNumber,
      scheduledDate: date,
      actualCastDate: "",
      totalCubicYards: 0,
      status: "planned" as const,
      notes: "",
      createdAt: now,
      updatedAt: now,
    };

    if (this._castingDayStore) {
      await this._castingDayStore.create(newDay);
    }
  };

  // Mark a mold assignment as poured
  markPoured = async (moldAssignmentId: string, actualDate: string) => {
    if (this._moldAssignmentStore) {
      await this._moldAssignmentStore.patch(moldAssignmentId, {
        status: "poured",
        updatedAt: new Date().toISOString(),
      });
    }

    // Also update the component
    const assignment = this.moldAssignments.find((ma) => ma.id === moldAssignmentId);
    if (assignment && this._componentStore) {
      await this._componentStore.patch(assignment.componentId, {
        status: "cast",
        actualCastDate: actualDate,
        updatedAt: new Date().toISOString(),
      });
    }
  };

  // Open schedule dialog with prefill
  openAssignDialog = (componentId: string) => {
    this.dialogPrefill = { componentId };
    this.rootStore.routerStore.openDialog({
      dialogName: "assignComponent",
    });
  };

  closeDialog = () => {
    this.dialogPrefill = null;
  };

  // Open the piece detail dialog for a specific mold assignment
  openMoldAssignmentDialog = (assignmentId: string) => {
    this.selectedMoldAssignmentId = assignmentId;
    this.rootStore.routerStore.openDialog({
      dialogName: "castingDayDetail",
    });
  };

  // --- Mass-push pieces by mold position column ---

  openPushPiecesDialog = () => {
    this.rootStore.routerStore.openDialog({ dialogName: "pushPieces" });
  };

  /** Eligible (planned) piece count per mold position for the current project filter. */
  get plannedCountsByMoldPosition(): Record<string, number> {
    const counts: Record<string, number> = { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0, "sump": 0 };
    const filteredStructIds = new Set(this.filteredStructures.map((s) => s.id));
    const componentIds = new Set(
      this.components.filter((c) => filteredStructIds.has(c.structureId)).map((c) => c.id)
    );
    for (const ma of this.moldAssignments) {
      if (ma.status !== "planned") continue;
      if (!componentIds.has(ma.componentId)) continue;
      counts[String(ma.moldPosition)] = (counts[String(ma.moldPosition)] || 0) + 1;
    }
    return counts;
  }

  // --- Piece label printing (QR-coded thermal labels) ---

  openPrintLabelsForPiece = (componentId: string) => {
    this.labelPrintContext = { mode: "single", componentIds: [componentId] };
    this.rootStore.routerStore.openDialog({ dialogName: "printLabels" });
  };

  openPrintLabelsForCastingDay = (castingDayId: string) => {
    this.labelPrintContext = { mode: "day", componentIds: [], castingDayId };
    this.rootStore.routerStore.openDialog({ dialogName: "printLabels" });
  };

  openPrintLabelsForShipmentLoad = (shipmentLoadId: string) => {
    this.labelPrintContext = { mode: "load", componentIds: [], shipmentLoadId };
    this.rootStore.routerStore.openDialog({ dialogName: "printLabels" });
  };

  // Update a casting day's status
  updateCastingDayStatus = async (
    dayId: string,
    status: "planned" | "in_progress" | "completed"
  ) => {
    const now = new Date().toISOString();
    const patch: Record<string, unknown> = { status, updatedAt: now };

    if (status === "completed") {
      patch.actualCastDate = now.slice(0, 10); // YYYY-MM-DD
    }

    if (this._castingDayStore) {
      await this._castingDayStore.patch(dayId, patch);
    }
  };

  // Update a mold assignment's status and sync to component
  updateMoldAssignmentStatus = async (
    assignmentId: string,
    status: "planned" | "poured" | "stripped" | "cured"
  ) => {
    const now = new Date().toISOString();

    if (this._moldAssignmentStore) {
      await this._moldAssignmentStore.patch(assignmentId, {
        status,
        updatedAt: now,
      });
    }

    // Sync component status
    const assignment = this.moldAssignments.find((ma) => ma.id === assignmentId);
    if (assignment && this._componentStore) {
      if (status === "poured") {
        await this._componentStore.patch(assignment.componentId, {
          status: "cast",
          actualCastDate: now.slice(0, 10),
          updatedAt: now,
        });
      } else if (status === "cured") {
        await this._componentStore.patch(assignment.componentId, {
          status: "cured",
          updatedAt: now,
        });
      }
    }
  };

  // Assign a mold assignment to a casting table
  assignToTable = async (moldAssignmentId: string, castingTableId: string) => {
    if (this._moldAssignmentStore) {
      await this._moldAssignmentStore.patch(moldAssignmentId, {
        castingTableId,
        updatedAt: new Date().toISOString(),
      });
    }
  };

  // Unassign a mold assignment from its casting table
  unassignFromTable = async (moldAssignmentId: string) => {
    if (this._moldAssignmentStore) {
      await this._moldAssignmentStore.patch(moldAssignmentId, {
        castingTableId: "",
        updatedAt: new Date().toISOString(),
      });
    }
  };

  // Assign a mold assignment to a yard zone
  assignToYardZone = async (moldAssignmentId: string, yardZoneId: string) => {
    if (this._moldAssignmentStore) {
      await this._moldAssignmentStore.patch(moldAssignmentId, {
        yardZoneId,
        updatedAt: new Date().toISOString(),
      });
    }
  };

  // Unassign a mold assignment from its yard zone
  unassignFromYardZone = async (moldAssignmentId: string) => {
    if (this._moldAssignmentStore) {
      await this._moldAssignmentStore.patch(moldAssignmentId, {
        yardZoneId: "",
        updatedAt: new Date().toISOString(),
      });
    }
  };

  // Assign a mold assignment to a shipment load
  assignToShipmentLoad = async (moldAssignmentId: string, shipmentLoadId: string) => {
    if (this._moldAssignmentStore) {
      await this._moldAssignmentStore.patch(moldAssignmentId, {
        shipmentLoadId,
        updatedAt: new Date().toISOString(),
      });
    }
  };

  // Unassign a mold assignment from its shipment load
  unassignFromShipmentLoad = async (moldAssignmentId: string) => {
    if (this._moldAssignmentStore) {
      await this._moldAssignmentStore.patch(moldAssignmentId, {
        shipmentLoadId: "",
        updatedAt: new Date().toISOString(),
      });
    }
  };

  // --- Estimate Actions ---

  createEstimate = async (data: {
    preparedBy: string;
    rateConcretePsCy: number;
    rateLaborPerPiece: number;
    rateEmbedPerPiece: number;
    rateLiftInsert: number;
    rateWaterstopPerLf: number;
    rateEngineeringFlat: number;
    rateShippingPerLoad: number;
    rateCranePerDay: number;
    estimatedLoads: number;
    estimatedCraneDays: number;
    notes: string;
  }) => {
    if (!this.selectedProjectId || !this._estimateStore) return;

    const now = new Date().toISOString();
    const existingCount = this.estimates.filter(
      (e) => e.projectId === this.selectedProjectId
    ).length;
    const estNumber = `EST-${new Date().getFullYear().toString().slice(-2)}-${String(
      100000 + existingCount + 1
    )}`;

    const newEstimate = {
      id: `sc-est-new-${Date.now()}`,
      projectId: this.selectedProjectId,
      estimateNumber: estNumber,
      revisionNumber: 1,
      status: "draft" as const,
      preparedBy: data.preparedBy,
      approvedBy: "",
      approvedDate: "",
      ...data,
      totalMaterialCost: 0,
      totalLaborCost: 0,
      totalEngineeringCost: data.rateEngineeringFlat,
      totalShippingCost: +(
        data.estimatedLoads * data.rateShippingPerLoad +
        data.estimatedCraneDays * data.rateCranePerDay
      ).toFixed(2),
      grandTotal: +(
        data.rateEngineeringFlat +
        data.estimatedLoads * data.rateShippingPerLoad +
        data.estimatedCraneDays * data.rateCranePerDay
      ).toFixed(2),
      createdAt: now,
      updatedAt: now,
    };

    await this._estimateStore.create(newEstimate);
  };

  addStructureToEstimate = async (
    estimateId: string,
    data: {
      markNumber: string;
      stationReference: string;
      overallLength: number;
      overallWidth: number;
      overallHeight: number;
      componentDefinitions: EstimateComponentDefinition[];
    }
  ) => {
    const estimate = this.getEstimate(estimateId);
    if (!estimate || !this._estimateLineItemStore) return;

    // Calculate component count from definitions
    const componentCount = data.componentDefinitions.reduce(
      (sum, cd) => sum + cd.quantity,
      0
    );

    // Estimate weight/CY from dimensions in inches (simplified formula)
    // Volume in in³ → cubic yards * fill factor
    const volumeIn3 = data.overallLength * data.overallWidth * data.overallHeight;
    const totalCubicYards = +(volumeIn3 / 46656 * 0.35).toFixed(2); // ~35% fill factor → CY
    const totalWeight = Math.round(totalCubicYards * 4050); // ~4050 lbs/CY for concrete

    // Cost calculations using estimate rate sheet
    const concreteCost = +(totalCubicYards * estimate.rateConcretePsCy).toFixed(2);
    // Rebar: per-piece rate * component count (stored in rateLaborPerPiece)
    const laborCost = +(componentCount * estimate.rateLaborPerPiece).toFixed(2);
    // Frame & Cover: per-piece rate * component count (stored in rateEmbedPerPiece)
    const embedCost = +(componentCount * estimate.rateEmbedPerPiece).toFixed(2);

    const liftInsertCost = 0;
    const waterstopCost = 0;

    const subtotal = +(
      concreteCost + laborCost + embedCost + liftInsertCost + waterstopCost
    ).toFixed(2);

    const now = new Date().toISOString();
    const newLineItem = {
      id: `sc-eli-new-${Date.now()}`,
      estimateId,
      structureId: "",
      markNumber: data.markNumber,
      componentCount,
      totalCubicYards,
      totalWeight,
      overallLength: data.overallLength,
      overallWidth: data.overallWidth,
      overallHeight: data.overallHeight,
      stationReference: data.stationReference,
      componentDefinitions: data.componentDefinitions,
      concreteCost,
      laborCost,
      embedCost,
      liftInsertCost,
      waterstopCost,
      subtotal,
      notes: "",
      createdAt: now,
      updatedAt: now,
    };

    await this._estimateLineItemStore.create(newLineItem);
    await this.recalculateEstimateTotals(estimateId);
  };

  removeLineItem = async (lineItemId: string) => {
    if (!this._estimateLineItemStore) return;
    const lineItem = this.estimateLineItems.find((li) => li.id === lineItemId);
    if (!lineItem) return;

    await this._estimateLineItemStore.remove(lineItemId);
    await this.recalculateEstimateTotals(lineItem.estimateId);
  };

  recalculateEstimateTotals = async (estimateId: string) => {
    const estimate = this.getEstimate(estimateId);
    if (!estimate || !this._estimateStore) return;

    const lineItems = this.getEstimateLineItems(estimateId);
    let totalMaterial = 0;
    let totalLabor = 0;

    for (const li of lineItems) {
      totalMaterial += li.concreteCost + li.embedCost + li.liftInsertCost + li.waterstopCost;
      totalLabor += li.laborCost;
    }

    const totalShippingCost = +(
      estimate.estimatedLoads * estimate.rateShippingPerLoad +
      estimate.estimatedCraneDays * estimate.rateCranePerDay
    ).toFixed(2);

    const grandTotal = +(
      totalMaterial + totalLabor + estimate.rateEngineeringFlat + totalShippingCost
    ).toFixed(2);

    await this._estimateStore.patch(estimateId, {
      totalMaterialCost: +totalMaterial.toFixed(2),
      totalLaborCost: +totalLabor.toFixed(2),
      totalEngineeringCost: estimate.rateEngineeringFlat,
      totalShippingCost,
      grandTotal,
      updatedAt: new Date().toISOString(),
    });
  };

  updateEstimateRateSheet = async (
    estimateId: string,
    rates: {
      rateConcretePsCy: number;
      rateLaborPerPiece: number;
      rateEmbedPerPiece: number;
      rateLiftInsert: number;
      rateWaterstopPerLf: number;
      rateEngineeringFlat: number;
      rateShippingPerLoad: number;
      rateCranePerDay: number;
      estimatedLoads: number;
      estimatedCraneDays: number;
    }
  ) => {
    if (!this._estimateStore || !this._estimateLineItemStore) return;

    // Update the estimate's rate sheet
    await this._estimateStore.patch(estimateId, {
      ...rates,
      updatedAt: new Date().toISOString(),
    });

    // Recalculate each line item with new rates
    const lineItems = this.getEstimateLineItems(estimateId);
    for (const li of lineItems) {
      const concreteCost = +(li.totalCubicYards * rates.rateConcretePsCy).toFixed(2);
      // Rebar: per-piece rate * component count
      const laborCost = +(li.componentCount * rates.rateLaborPerPiece).toFixed(2);
      // Frame & Cover: per-piece rate * component count
      const embedCost = +(li.componentCount * rates.rateEmbedPerPiece).toFixed(2);
      const liftInsertCost = 0;
      const waterstopCost = 0;
      const subtotal = +(
        concreteCost + laborCost + embedCost + liftInsertCost + waterstopCost
      ).toFixed(2);

      await this._estimateLineItemStore.patch(li.id, {
        concreteCost,
        laborCost,
        embedCost,
        liftInsertCost,
        waterstopCost,
        subtotal,
        updatedAt: new Date().toISOString(),
      });
    }

    await this.recalculateEstimateTotals(estimateId);
  };

  submitEstimate = async (estimateId: string) => {
    if (!this._estimateStore) return;
    await this._estimateStore.patch(estimateId, {
      status: "submitted",
      updatedAt: new Date().toISOString(),
    });
  };

  approveEstimate = async (estimateId: string, approvedBy: string) => {
    if (!this._estimateStore) return;
    const now = new Date().toISOString();
    await this._estimateStore.patch(estimateId, {
      status: "approved",
      approvedBy,
      approvedDate: now,
      updatedAt: now,
    });
  };

  rejectEstimate = async (estimateId: string) => {
    if (!this._estimateStore) return;
    await this._estimateStore.patch(estimateId, {
      status: "draft",
      updatedAt: new Date().toISOString(),
    });
  };

  convertEstimateToProduction = async (estimateId: string) => {
    const estimate = this.getEstimate(estimateId);
    if (!estimate || !this._estimateStore || !this._structureStore || !this._componentStore) return;

    const lineItems = this.getEstimateLineItems(estimateId);
    const now = new Date().toISOString();

    for (const li of lineItems) {
      // Create structure
      const structureId = `sc-struct-est-${Date.now()}-${li.id}`;
      const newStructure = {
        id: structureId,
        projectId: estimate.projectId,
        markNumber: li.markNumber,
        stationReference: li.stationReference || "",
        overallLength: li.overallLength || 0,
        overallWidth: li.overallWidth || 0,
        overallHeight: li.overallHeight || 0,
        totalWeight: li.totalWeight,
        totalCubicYards: li.totalCubicYards,
        status: "planned" as const,
        notes: `Created from estimate ${estimate.estimateNumber}`,
        createdAt: now,
        updatedAt: now,
      };
      await this._structureStore.create(newStructure);

      // Create components from componentDefinitions
      let sortOrder = 1;
      for (const def of li.componentDefinitions) {
        for (let q = 0; q < def.quantity; q++) {
          const suffixMap: Record<string, string> = {
            base: "BASE",
            riser: "RISER",
            cone: "CONE",
            flat_top: "GR",
          };
          const suffix = suffixMap[def.componentType] || def.componentType.toUpperCase();
          const controlNumber = `${suffix}-${String.fromCharCode(65 + sortOrder - 1)}`;

          const newComponent = {
            id: `sc-comp-est-${Date.now()}-${sortOrder}`,
            structureId,
            controlNumber,
            componentType: def.componentType,
            heightFeet: def.heightFeet,
            heightInches: def.heightInches,
            heightFraction: def.heightFraction,
            length: li.overallLength || 0,
            width: li.overallWidth || 0,
            weight: Math.round(li.totalWeight / li.componentCount),
            cubicYards: +(li.totalCubicYards / li.componentCount).toFixed(2),
            sortOrder,
            status: "pending" as const,
            scheduledDate: "",
            actualCastDate: "",
            castingGroupId: "",
            castingDayId: "",
            moldPosition: def.componentType === "flat_top" ? ("sump" as const) : ((sortOrder % 5) + 1),
            createdAt: now,
            updatedAt: now,
          };
          await this._componentStore.create(newComponent);
          sortOrder++;
        }
      }

      // Link line item to the new structure
      if (this._estimateLineItemStore) {
        await this._estimateLineItemStore.patch(li.id, {
          structureId,
          updatedAt: now,
        });
      }
    }

    // Mark estimate as converted
    await this._estimateStore.patch(estimateId, {
      status: "converted",
      updatedAt: now,
    });
  };

  // --- Estimate Dialog Helpers ---

  openCreateEstimateDialog = () => {
    this.estimateDialogContext = {};
    this.rootStore.routerStore.openDialog({ dialogName: "createEstimate" });
  };

  openAddStructureDialog = (estimateId: string) => {
    this.estimateDialogContext = { estimateId };
    this.rootStore.routerStore.openDialog({ dialogName: "addEstimateStructure" });
  };

  openEditRateSheetDialog = (estimateId: string) => {
    this.estimateDialogContext = { estimateId };
    this.rootStore.routerStore.openDialog({ dialogName: "editRateSheet" });
  };

  openSubmitEstimateDialog = (estimateId: string) => {
    this.estimateDialogContext = { estimateId };
    this.rootStore.routerStore.openDialog({ dialogName: "submitEstimate" });
  };

  openApproveEstimateDialog = (estimateId: string) => {
    this.estimateDialogContext = { estimateId };
    this.rootStore.routerStore.openDialog({ dialogName: "approveEstimate" });
  };

  openConvertEstimateDialog = (estimateId: string) => {
    this.estimateDialogContext = { estimateId };
    this.rootStore.routerStore.openDialog({ dialogName: "convertEstimate" });
  };

  // --- Load data ---

  loadData = async () => {
    this.loading = true;
    this.error = null;
    try {
      await Promise.all([
        this._clientStore?.find(),
        this._projectStore?.find(),
        this._structureStore?.find(),
        this._componentStore?.find(),
        this._castingGroupStore?.find(),
        this._castingDayStore?.find(),
        this._moldAssignmentStore?.find(),
        this._shipmentStore?.find(),
        this._shipmentLoadStore?.find(),
        this._preProductionMeetingStore?.find(),
        this._castingTableStore?.find(),
        this._yardZoneStore?.find(),
        this._estimateStore?.find(),
        this._estimateLineItemStore?.find(),
        this._qcInspectionStore?.find(),
        this._batchTicketStore?.find(),
        this._rawMaterialStore?.find(),
        this._vendorStore?.find(),
        this._purchaseOrderStore?.find(),
        this._poLineItemStore?.find(),
      ]);
      runInAction(() => {
        this.loading = false;
      });
    } catch (err: unknown) {
      runInAction(() => {
        this.error = err instanceof Error ? err.message : "Failed to load schedule data";
        this.loading = false;
      });
    }
  };
}

export default StructureCastProductionScheduleStore;
